/** @jsx jsx */
/** @jsxfrag */
import React from 'react';

import { css, jsx } from '@emotion/react';
import { FormattedMessage } from 'react-intl-next';

import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useACTAnalyticsEvents } from '@atlassian/ad-control-toolkit';

import { CLICK_PRIVACY_POLICY_LINK } from '../../common/constants';
import { type ProductId } from '../../common/types';
import { useAppRecommendationsProvider } from '../../controllers';
import { messages } from '../messages';

// The following spacing units are in pixels because trello has root font size set to 14
const ulStyles = xcss({
	listStyleType: 'disc',
	paddingLeft: 'space.250',
	marginTop: 'space.150',
});

const pStyles = css({
	marginTop: token('space.150', '12px'),
});

type ReasonsModalBodyProps = {
	productId: ProductId;
	name: string;
	reasons?: string[];
};

const ReasonsModalBody: React.FC<ReasonsModalBodyProps> = ({ productId, name, reasons = [] }) => {
	const { onEvents } = useAppRecommendationsProvider();
	const { createInteractedUIEvent } = useACTAnalyticsEvents();

	const onClickPrivacyLink = React.useCallback(() => {
		onEvents?.({
			type: CLICK_PRIVACY_POLICY_LINK,
			itemId: productId,
			analyticsEvent: createInteractedUIEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'adControlsReasonsClose',
			}),
		});
	}, [createInteractedUIEvent, onEvents, productId]);

	return (
		<Box>
			<FormattedMessage
				{...messages.reasonsIntro}
				values={{
					product: <strong>{name}</strong>,
				}}
			/>
			<Stack as="ul" xcss={ulStyles}>
				{reasons.map((reason: string) => (
					// eslint-disable-next-line @atlaskit/design-system/prefer-primitives
					<li key={reason}>{reason}</li>
				))}
			</Stack>
			<p css={pStyles}>
				<a
					onClick={onClickPrivacyLink}
					href="https://www.atlassian.com/legal/privacy-policy"
					target="_blank"
				>
					<FormattedMessage {...messages.learnMoreAboutDataPolicy} />
				</a>
			</p>
		</Box>
	);
};

export default ReasonsModalBody;
